import { useEffect, useState } from 'react';
import { mailingApi, type MailingSpace, type SubscriptionState } from '../api/mailing';
import { MailMark, MailStatusDot } from './MailStatusDot';

const cache = new Map<string, Promise<SubscriptionState | null>>();
const queue = new Map<string, Array<(s: SubscriptionState | null) => void>>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(space: MailingSpace) {
  timer = null;
  const batch = new Map(queue);
  queue.clear();
  mailingApi(space)
    .subscriptions([...batch.keys()])
    .then((rows) => {
      const byEmail = new Map(rows.map((r) => [r.email, r]));
      for (const [email, cbs] of batch) cbs.forEach((cb) => cb(byEmail.get(email) ?? null));
    })
    .catch(() => {
      for (const [email, cbs] of batch) {
        cache.delete(`${space}:${email}`);
        cbs.forEach((cb) => cb(null));
      }
    });
}

function load(space: MailingSpace, email: string): Promise<SubscriptionState | null> {
  const key = `${space}:${email}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = new Promise<SubscriptionState | null>((resolve) => {
    queue.set(email, [...(queue.get(email) ?? []), resolve]);
    if (!timer) timer = setTimeout(() => flush(space), 30);
  });
  cache.set(key, p);
  return p;
}

export function forgetMailingSubscription(email: string, space: MailingSpace = 'affilmaster') {
  cache.delete(`${space}:${email.trim().toLowerCase()}`);
}

export function MailStatusMark({ email, space = 'affilmaster' }: { email: string | null | undefined; space?: MailingSpace }) {
  const [state, setState] = useState<SubscriptionState | null>(null);
  const [version, setVersion] = useState(0);
  const normalized = (email ?? '').trim().toLowerCase();

  useEffect(() => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) return;
    let cancelled = false;
    load(space, normalized).then((s) => !cancelled && setState(s));
    return () => { cancelled = true; };
  }, [normalized, space, version]);

  if (!state?.status) return null;
  return (
    <MailStatusDot
      status={state.status}
      onClear={async () => {
        await mailingApi(space).clearContactStatus(normalized);
        forgetMailingSubscription(normalized, space);
        setVersion((v) => v + 1);
      }}
    />
  );
}

export function MailingSubscriptionBadge({ email, space = 'affilmaster' }: { email: string | null | undefined; space?: MailingSpace }) {
  const [state, setState] = useState<SubscriptionState | null>(null);
  const [version, setVersion] = useState(0);
  const normalized = (email ?? '').trim().toLowerCase();

  useEffect(() => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) return;
    let cancelled = false;
    load(space, normalized).then((s) => !cancelled && setState(s));
    return () => { cancelled = true; };
  }, [normalized, space, version]);

  async function clearStatus() {
    await mailingApi(space).clearContactStatus(normalized);
    forgetMailingSubscription(normalized, space);
    setVersion((v) => v + 1);
  }

  if (!state) return null;
  const title = state.subscribed
    ? 'Přihlášen k odběru mailingu'
    : `Odhlášen z mailingu${state.unsubscribedAt ? ` ${new Date(state.unsubscribedAt).toLocaleDateString('cs-CZ')}` : ''}${state.source === 'spamreport' ? ' (nahlásil jako spam)' : ''}`;
  const segments = state.segments ?? [];
  const unsubscribedDay = state.unsubscribedAt ? new Date(state.unsubscribedAt).toLocaleDateString('cs-CZ') : '';
  return (
    <>
      <MailStatusDot status={state.status} onClear={clearStatus} />
      {!state.subscribed && (
        <MailMark tone="unsubscribed" label={state.source === 'spamreport' ? 'Odhlášen · spam' : 'Odhlášen'} title={`Odhlášen z mailingu${unsubscribedDay ? ` ${unsubscribedDay}` : ''}${state.source === 'spamreport' ? ' (nahlásil jako spam)' : ''}. Kampaně se mu neposílají, odhlášení se nevrací.`} />
      )}
      {state.subscribed && <span
        title={title}
        style={{
          display: 'inline-flex', alignItems: 'center', marginLeft: 6, padding: '1px 7px', borderRadius: 999,
          fontSize: 10, fontWeight: 650, lineHeight: 1.6, verticalAlign: 'middle', whiteSpace: 'nowrap',
          background: '#e8f4ed', color: '#137333',
        }}
      >
        ✓ mailing
      </span>}
      {segments.map((seg) => (
        <span
          key={seg.name}
          title={`Je ve skupině ${seg.name}`}
          style={{
            display: 'inline-flex', alignItems: 'center', marginLeft: 4, padding: '1px 7px', borderRadius: 999,
            fontSize: 10, fontWeight: 600, lineHeight: 1.6, verticalAlign: 'middle', whiteSpace: 'nowrap',
            background: `${seg.color ?? '#6b7280'}1a`, color: seg.color ?? '#4b5563',
          }}
        >
          {seg.name}
        </span>
      ))}
    </>
  );
}
